import React, { memo } from 'react'
import { Select } from 'components/forms/Selects'
import { Spinner } from 'transfix-ui/components/Spinner'
import SuggestLocation, { ILocationSuggestion } from 'transfix-ui/components/SuggestLocation'
import { ICarrierCapacity } from 'apps/marketplace/store/contractLanes/types'
import { DEFAULT_RADIUS, RADIUS_OPTIONS } from 'apps/marketplace/utils/constants/radii'
import { ChangeRateData } from 'apps/marketplace/utils/helpers/useCarrier'
import { TextInput } from 'components/atoms'
import { capitalize } from 'utils/capitalize'
import { FORMATS } from 'constants/datetime'

interface ICapacityEntryProps {
  dayOfWeek: string
  value: number | string
  onChange: (arg: React.ChangeEvent<HTMLInputElement>) => void
}

interface ICapacityEditorProps {
  values: {
    [key: string]: string
  }
  onChange: (arg: React.ChangeEvent<HTMLInputElement>) => void
}

interface ICapacityProps {
  carrierId: string
  carrierCapacity?: ICarrierCapacity
  pickupLocation: {
    city: string
    state: string
  }
  onChange: (changes: Partial<ChangeRateData>) => void
  displayLink: boolean
}

const CapacityEntry = memo<ICapacityEntryProps>(({ dayOfWeek, onChange, value }) => (
  <div className='mr1'>
    <TextInput
      label={capitalize(dayOfWeek, false, 3)}
      id={dayOfWeek}
      type='number'
      value={value}
      onChange={onChange}
      dataTest={`rate-${dayOfWeek}`}
      placeholder='0'
      min='0'
    />
  </div>
))

const CapacityEditor = memo<ICapacityEditorProps>(({ onChange, values }) => (
  <div className='flex' data-test='day-rate-entries'>
    {FORMATS.fullWeekList.map(dayOfWeek => (
      <CapacityEntry
        key={dayOfWeek}
        dayOfWeek={dayOfWeek}
        value={values[`${dayOfWeek}Limit`]}
        onChange={onChange}
      />
    ))}
  </div>
))

export const Capacity = memo<ICapacityProps>(
  ({ carrierCapacity, carrierId, displayLink, onChange, pickupLocation }) => {
    const limitValueHandler = ({ target: { id, value } }: React.ChangeEvent<HTMLInputElement>) => {
      const { city, state } = pickupLocation
      const { pickupCity, pickupRadius, pickupStateCode, values } = carrierCapacity || {}
      onChange({
        carrierCapacity: {
          values: { ...values, [`${id}Limit`]: parseInt(value, 10) || '' },
          pickupCity: pickupCity && pickupStateCode ? pickupCity : city,
          pickupStateCode: pickupCity && pickupStateCode ? pickupStateCode : state,
          pickupRadius: pickupRadius || DEFAULT_RADIUS,
        },
      })
    }

    const handleCapacityPickupChange = ({ city, state }: ILocationSuggestion) => {
      onChange({
        carrierCapacity: {
          ...carrierCapacity,
          pickupCity: city,
          pickupStateCode: state,
        },
      })
    }

    const radiusHandler = ({ value }: { value: string | number }) => {
      onChange({
        carrierCapacity: {
          ...carrierCapacity,
          pickupRadius: parseInt(String(value), 10),
        },
      })
    }

    const radiusValue = carrierCapacity?.pickupRadius || DEFAULT_RADIUS
    return (
      <>
        {carrierId && <h3>Capacity</h3>}
        {carrierCapacity?.loading && (
          <div className='row'>
            <div className='col-md-9'>
              <Spinner position='relative' />
            </div>
          </div>
        )}
        {!carrierCapacity?.loading && carrierCapacity?.alreadySet && (
          <div className='row'>
            <div className='col-xs-12' data-test='carrier-capacity-summary'>
              <p>{carrierCapacity.summary}</p>
              <p className='text-secondary'>
                Capacity in {carrierCapacity.pickupCity}, {carrierCapacity.pickupStateCode} &middot;
                Trucks pick up within {carrierCapacity.pickupRadius} mi.
                {displayLink && (
                  <a
                    href={`${__ROOT__}/carriers/${carrierId}`}
                    target='_blank'
                    rel='noopener noreferrer'>
                    {' '}
                    &nbsp;Edit capacity
                  </a>
                )}
              </p>
            </div>
          </div>
        )}
        {!carrierCapacity?.loading && !carrierCapacity?.alreadySet && carrierId && (
          <>
            <div className='row'>
              <div className='col-md-8'>
                <p>How many trucks are available?</p>
                <CapacityEditor
                  values={carrierCapacity?.values || {}}
                  onChange={limitValueHandler}
                />
              </div>
            </div>

            <div className='row'>
              <div className='col-md-4'>
                <SuggestLocation
                  label='Where are trucks available?'
                  id='truck-availability'
                  placeholder='Pickup City'
                  value={pickupLocation}
                  onChange={handleCapacityPickupChange}
                  resultTypes={['City', 'PostalCode']}
                />
              </div>
            </div>

            <div className='row'>
              <div className='col-md-4'>
                <Select
                  id='radius-dropdown'
                  label='How far are they willing to pick up?'
                  options={RADIUS_OPTIONS}
                  value={RADIUS_OPTIONS.find(entry => entry.value === radiusValue)}
                  onChange={radiusHandler}
                  dataTest='add-radius'
                />
              </div>
            </div>
          </>
        )}
      </>
    )
  }
)
